/**
 * @file SearchCustomerModel.jsx
 * @description SearchCustomerModel component for displaying and managing search customer model.
 * @component SearchCustomerModel
 * @exports SearchCustomerModel - Main component for displaying and managing search customer model.
 */

import {
  Modal,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
} from "@mui/material";
import React, { useState } from "react";
import {
  VARIANTS,
  BUTTON_COLORS,
  SIZES,
  STATUS,
  TYPOGRAPHY_VARIANT,
} from "../../../common/constants/commonConstants";
import {
  INCOMING_CALLS_STRINGS,
  INCOMING_CALLS_ERRORS,
  VALIDATION_MESSAGES,
  VALIDATION_PATTERNS,
  SEARCH_FILTER_OPTIONS,
  SEARCH_FILTER_LABELS,
  UI_CONFIG,
} from "../../../common/constants/agentModule/incomingCallsConstants";
import {
  getcustomerprofilebylastname,
  getcustomerprofilebyphone,
  getcustomerprofilebyemail,
} from "../../../services/customerinfoModule/profileServices";

const SearchCustomerModel = ({ open, onClose, onCustomerGuid }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const [error, setError] = useState("");
  const [customerData, setCustomerData] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const validateSearchText = () => {
    if (!searchText.trim()) {
      setError(VALIDATION_MESSAGES.SEARCH_TEXT_REQUIRED);
      return false;
    }

    if (!filter) {
      setError(VALIDATION_MESSAGES.SELECT_FILTER);
      return false;
    }

    switch (filter) {
      case SEARCH_FILTER_OPTIONS.LAST_NAME: {
        const nameRegex = VALIDATION_PATTERNS.LAST_NAME;
        if (!nameRegex.test(searchText.trim())) {
          setError(VALIDATION_MESSAGES.INVALID_LAST_NAME);
          return false;
        }
        break;
      }
      case SEARCH_FILTER_OPTIONS.PHONE_NUMBER: {
        const phoneRegex = VALIDATION_PATTERNS.PHONE_NUMBER;
        const cleanPhone = searchText.replace(/[\s\-\(\)]/g, "");
        if (!phoneRegex.test(cleanPhone)) {
          setError(VALIDATION_MESSAGES.INVALID_PHONE_NUMBER);
          return false;
        }
        break;
      }
      case SEARCH_FILTER_OPTIONS.EMAIL: {
        const emailRegex = VALIDATION_PATTERNS.EMAIL;
        if (!emailRegex.test(searchText.trim())) {
          setError(VALIDATION_MESSAGES.INVALID_EMAIL);
          return false;
        }
        break;
      }
      default:
        setError(VALIDATION_MESSAGES.INVALID_FILTER);
        return false;
    }

    setError("");
    return true;
  };

  const handleSearch = async () => {
    if (!validateSearchText()) {
      return;
    }

    setIsLoading(true);
    setCustomerData(null);
    setSelectedCustomer(null);
    try {
      let searchResult = null;

      switch (filter) {
        case SEARCH_FILTER_OPTIONS.LAST_NAME:
          searchResult = await getcustomerprofilebylastname({
            customerlastname: searchText.trim(),
          });
          break;
        case SEARCH_FILTER_OPTIONS.PHONE_NUMBER:
          searchResult = await getcustomerprofilebyphone({
            customerphone: searchText.trim(),
          });
          break;
        case SEARCH_FILTER_OPTIONS.EMAIL:
          searchResult = await getcustomerprofilebyemail({
            customeremail: searchText.trim(),
          });
          break;
        default:
          throw new Error("Invalid filter selection");
      }

      if (searchResult && searchResult.length > 0) {
        setCustomerData(searchResult);
        if (searchResult.length === 1) {
          onCustomerGuid(searchResult[0].customerguid);
        }
      } else {
        setError("No customer found with the provided information.");
        return;
      }
    } catch (error) {
      console.error("Search error:", error);
      setError(INCOMING_CALLS_ERRORS.SEARCH_FAILED);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
  };

  const handleConfirmSelection = () => {
    if (
      selectedCustomer &&
      selectedCustomer.customerguid &&
      typeof onCustomerGuid === "function"
    ) {
      onCustomerGuid(selectedCustomer.customerguid);
      onClose();
    }
  };

  const handleClose = () => {
    setCustomerData(null);
    setSelectedCustomer(null);
    setError("");
    onClose();
  };

  const getPlaceholder = () => {
    switch (filter) {
      case SEARCH_FILTER_OPTIONS.LAST_NAME:
        return INCOMING_CALLS_STRINGS.ENTER_LAST_NAME;
      case SEARCH_FILTER_OPTIONS.PHONE_NUMBER:
        return INCOMING_CALLS_STRINGS.ENTER_PHONE_NUMBER;
      case SEARCH_FILTER_OPTIONS.EMAIL:
        return INCOMING_CALLS_STRINGS.ENTER_EMAIL_ADDRESS;
      default:
        return INCOMING_CALLS_STRINGS.ENTER_SEARCH_TEXT;
    }
  };

  return (
    <div>
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            width: (customerData && customerData.length > 0) ? UI_CONFIG.MODAL_MAX_WIDTH : UI_CONFIG.MODAL_WIDTH,
            maxHeight: "80vh",
            margin: "auto",
            padding: UI_CONFIG.MODAL_PADDING,
            marginTop: UI_CONFIG.MODAL_MARGIN_TOP,
            backgroundColor: "white",
            borderRadius: UI_CONFIG.MODAL_BORDER_RADIUS,
            overflow: "auto",
          }}
        >
          <Typography variant={TYPOGRAPHY_VARIANT.H6}>
            {INCOMING_CALLS_STRINGS.SEARCH_CUSTOMER_TITLE}
          </Typography>

          {error && (
            <Alert
              severity={STATUS.ERROR}
              sx={{ mt: UI_CONFIG.MARGIN_TOP, mb: 1 }}
            >
              {error}
            </Alert>
          )}

          <FormControl
            variant={VARIANTS.OUTLINED}
            size={SIZES.SMALL}
            sx={{ width: "100%", mt: UI_CONFIG.MARGIN_TOP }}
          >
            <InputLabel>Search with</InputLabel>
            <Select
              size={SIZES.SMALL}
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setError(""); // Clear error when filter changes
              }}
              label="Search with"
            >
              <MenuItem value={SEARCH_FILTER_OPTIONS.LAST_NAME}>
                {SEARCH_FILTER_LABELS[SEARCH_FILTER_OPTIONS.LAST_NAME]}
              </MenuItem>
              <MenuItem value={SEARCH_FILTER_OPTIONS.PHONE_NUMBER}>
                {SEARCH_FILTER_LABELS[SEARCH_FILTER_OPTIONS.PHONE_NUMBER]}
              </MenuItem>
              <MenuItem value={SEARCH_FILTER_OPTIONS.EMAIL}>
                {SEARCH_FILTER_LABELS[SEARCH_FILTER_OPTIONS.EMAIL]}
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            variant={VARIANTS.OUTLINED}
            size={SIZES.SMALL}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setError(""); // Clear error when text changes
            }}
            placeholder={getPlaceholder()}
            sx={{ my: 2 }}
          />

          <Button
            variant={VARIANTS.CONTAINED}
            color={BUTTON_COLORS.PRIMARY}
            onClick={handleSearch}
            disabled={isLoading}
            sx={{ mr: 1 }}
          >
            {isLoading
              ? INCOMING_CALLS_STRINGS.SEARCHING
              : INCOMING_CALLS_STRINGS.SEARCH}
          </Button>
          <Button variant={VARIANTS.OUTLINED} onClick={handleClose}>
            {INCOMING_CALLS_STRINGS.CANCEL}
          </Button>

          {customerData && customerData.length > 1 && (
            <>
              <Typography  sx={{ mt: 2, mb: 1, fontSize: '14' }}>
                {customerData.length === 1
                  ? "Customer Found"
                  : `Found ${customerData.length} Customers`}
              </Typography>

              <TableContainer component={Paper} sx={{ maxHeight: 300, mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox"></TableCell>
                      <TableCell>First Name</TableCell>
                      <TableCell>Last Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Email</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customerData.map((customer, index) => (
                      <TableRow
                        key={index}
                        hover
                        onClick={() => handleCustomerSelect(customer)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell padding="checkbox">
                          <Radio
                            checked={
                              selectedCustomer?.customerguid ===
                              customer.customerguid
                            }
                            onChange={() => handleCustomerSelect(customer)}
                          />
                        </TableCell>
                        <TableCell>{customer.firstname || "-"}</TableCell>
                        <TableCell>{customer.lastname || "-"}</TableCell>
                        <TableCell>
                          {customer.customeruniqueid || "-"}
                        </TableCell>
                        <TableCell>{customer.emailaddress || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant={VARIANTS.CONTAINED}
                  color={BUTTON_COLORS.PRIMARY}
                  onClick={handleConfirmSelection}
                  disabled={!selectedCustomer}
                  sx={{ mr: 1 }}
                >
                  Select Customer
                </Button>
                <Button variant={VARIANTS.OUTLINED} onClick={handleClose}>
                  {INCOMING_CALLS_STRINGS.CANCEL}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default SearchCustomerModel;
